import { useCallback, useMemo, useState } from 'react'
import axios from 'axios'
import { BrokerType, ClientMyBroker, isPublicEmailDomain } from '@numeo/types-core'
import { brokersApi } from 'api/brokers.api'
import { useClassifiedBrokers } from './useClassifiedBrokers'
import { useMyBrokers, refetchMyBrokers, updateMyBrokerStatus, upsertMyBroker } from './useMyBrokers'
import { showError, showSuccess } from 'utils/snackbar'

/** Direction of a broker-status change, emitted after a successful mutation. */
type BrokerStatusChange = 'favorited' | 'unfavorited' | 'blacklisted' | 'unblacklisted'

interface UseBrokerStatusActionOptions {
    /** MC number — the canonical key for favorite/blacklist. */
    brokerMc: string | null | undefined
    /** Broker email — the ALTERNATE key for sources whose loads carry no MC (the box-truck Sylectus
     *  feed). Matched against the hydrated broker's emails; creation stores it via `emails: [email]`. */
    brokerEmail?: string | null
    /** Display name used in snackbars and on broker creation. */
    brokerName?: string | null
    /** Fired after any successful status change (favorite / blacklist / clear). */
    onStatusChange?: (change: BrokerStatusChange) => void
    /** Fired after any successful mutation, regardless of direction (e.g. to reload a list). */
    onComplete?: () => void
}

/**
 * Favorite / blacklist a broker by MC number — mutation logic behind the load-hub BrokerStatusChip.
 * The my_brokers row is resolved (or created) server-side, then optimistically mirrored into the
 * shared useMyBrokers cache so every consumer (badges, other rows) reflects the change instantly.
 */
/**
 * Finds the broker behind an email-keyed row (loads with no MC). The server's create dedupes globally by the
 * email's BUSINESS domain and by company name — a row added elsewhere (e.g. blacklisted in load-hub by MC)
 * exists without this exact email, and a POST for it would 409. Mirror that matching so such rows resolve to
 * an update instead — INCLUDING the public-domain skip: extraction filters gmail & co before storing, but a
 * single legacy row carrying one would otherwise soak up every load posted from that provider.
 */
const matchByEmail = (brokers: ClientMyBroker[], emailLc: string, brokerName: string | null | undefined): ClientMyBroker | null => {
    const byEmail = brokers.find((b) => [...(b.emails ?? []), ...(b.additionalUpdateEmails ?? [])].some((e) => e.toLowerCase() === emailLc))
    if (byEmail) return byEmail
    const emailDomain = emailLc.split('@')[1] || null
    const byDomain =
        emailDomain && !isPublicEmailDomain(emailDomain)
            ? brokers.find((b) => b.domain === emailDomain || (b.additionalDomains ?? []).includes(emailDomain))
            : null
    if (byDomain) return byDomain
    const nameLc = brokerName?.trim().toLowerCase()
    return nameLc ? (brokers.find((b) => b.companyName?.trim().toLowerCase() === nameLc) ?? null) : null
}

export const useBrokerStatusAction = ({ brokerMc, brokerEmail, brokerName, onStatusChange, onComplete }: UseBrokerStatusActionOptions) => {
    const { brokers: mcBrokers } = useMyBrokers([brokerMc])
    const { brokers: classifiedBrokers } = useClassifiedBrokers()
    const [loading, setLoading] = useState(false)

    const emailLc = brokerEmail?.trim().toLowerCase() || null
    const matchedBroker = useMemo(() => {
        if (brokerMc) return mcBrokers.find((b) => b.mcNumber === brokerMc) ?? null
        return emailLc ? matchByEmail(classifiedBrokers, emailLc, brokerName) : null
    }, [brokerMc, emailLc, brokerName, mcBrokers, classifiedBrokers])
    const status = matchedBroker?.type ?? null
    const isBlacklisted = status === BrokerType.IN_BLACKLIST
    const isFavorite = status === BrokerType.FAVORITE

    const applyStatus = useCallback(
        async (newType: BrokerType | null) => {
            if (!brokerMc && !emailLc) return
            setLoading(true)
            const displayName = brokerName || brokerMc || brokerEmail || ''
            try {
                let resolvedId = matchedBroker?.id ?? null

                if (!resolvedId && brokerMc) {
                    try {
                        const res = await axios.get<ClientMyBroker>(`/my-brokers/by-mc/${brokerMc}`)
                        resolvedId = res.data?.id ?? null
                    } catch (lookupErr) {
                        console.warn('[useBrokerStatusAction] broker lookup failed, will attempt create', lookupErr)
                    }
                } else if (!resolvedId && emailLc && brokerName?.trim()) {
                    // Only favorite/blacklisted brokers are cached; an unclassified one is found by searching its name.
                    try {
                        const { items } = await brokersApi.listBrokers({ q: brokerName.trim(), limit: 20 })
                        resolvedId = matchByEmail(items, emailLc, brokerName)?.id ?? null
                    } catch (lookupErr) {
                        console.warn('[useBrokerStatusAction] broker name search failed, will attempt create', lookupErr)
                    }
                }

                if (newType === null) {
                    // Remove status → set to UNCLASSIFIED
                    if (resolvedId) {
                        await axios.put(`/my-brokers/${resolvedId}`, { type: BrokerType.UNCLASSIFIED })
                    }
                    if (brokerMc) updateMyBrokerStatus(brokerMc, null, { companyName: brokerName || undefined })
                    else refetchMyBrokers() // email-keyed rows have no mc for the optimistic patch — reload the small cache
                    showSuccess(`Broker "${displayName}" status removed`)
                } else if (resolvedId) {
                    await axios.put(`/my-brokers/${resolvedId}`, { type: newType })
                    if (brokerMc) updateMyBrokerStatus(brokerMc, newType, { id: resolvedId, companyName: brokerName || undefined })
                    else refetchMyBrokers()
                    showSuccess(`Broker "${displayName}" ${newType === BrokerType.IN_BLACKLIST ? 'blacklisted' : 'added to favorites'}`)
                } else {
                    // Create — MC when the load has one, else the broker email (box-truck Sylectus feed);
                    // the server requires companyName OR mcNumber, and Sylectus always carries the company.
                    const createRes = await axios.post<ClientMyBroker>('/my-brokers/', {
                        mcNumber: brokerMc || undefined,
                        companyName: brokerName || undefined,
                        emails: !brokerMc && brokerEmail ? [brokerEmail] : undefined,
                        type: newType,
                    })
                    resolvedId = createRes.data?.id ?? null
                    upsertMyBroker(createRes.data)
                    showSuccess(`Broker "${displayName}" ${newType === BrokerType.IN_BLACKLIST ? 'blacklisted' : 'added to favorites'}`)
                }

                // Direction from the requested type, falling back to the prior status when clearing.
                const prevType = matchedBroker?.type ?? null
                const change: BrokerStatusChange | null =
                    newType === BrokerType.FAVORITE
                        ? 'favorited'
                        : newType === BrokerType.IN_BLACKLIST
                          ? 'blacklisted'
                          : prevType === BrokerType.FAVORITE
                            ? 'unfavorited'
                            : prevType === BrokerType.IN_BLACKLIST
                              ? 'unblacklisted'
                              : null
                if (change) onStatusChange?.(change)

                onComplete?.()
            } catch (err) {
                console.error('[useBrokerStatusAction] failed to update broker status', err)
                if (axios.isAxiosError(err) && err.response?.status === 409) {
                    // Create hit the server's global dedupe (same domain/company under another key). Pull
                    // the fresh list so the row can resolve locally, and say what actually happened.
                    refetchMyBrokers()
                    showError('This broker is already in your list — reopen the menu to change its status')
                } else {
                    showError('Failed to update broker status')
                }
            } finally {
                setLoading(false)
            }
        },
        [brokerMc, brokerEmail, emailLc, brokerName, matchedBroker, onStatusChange, onComplete]
    )

    return { status, isBlacklisted, isFavorite, loading, applyStatus }
}
